import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Check, X, Zap, Crown, Building2, ArrowRight, CalendarDays, Monitor, FolderKanban } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = process.env.REACT_APP_BACKEND_URL;

const MODULE_META = {
  event_center: { label: "GLOTECH Event Center", icon: CalendarDays, color: "#F59E0B", tagline: "Gerez vos evenements de A a Z" },
  web: { label: "GLOTECH Web", icon: Monitor, color: "#0099CC", tagline: "CRM, facturation et presence digitale" },
  projects: { label: "GLOTECH Projects", icon: FolderKanban, color: "#10B981", tagline: "Gestion de projets et livrables" },
};

const PLAN_ICONS = { free: Zap, pro: Crown, enterprise: Building2 };
const PLAN_LABELS = { free: "Gratuit", pro: "Pro", enterprise: "Enterprise" };
const PLAN_BADGE_COLORS = {
  free: "bg-[#64748B]/20 text-[#94A3B8]",
  pro: "bg-[#F59E0B]/20 text-[#F59E0B]",
  enterprise: "bg-[#8B5CF6]/20 text-[#8B5CF6]",
};

export default function PricingPage() {
  const { module } = useParams();
  const navigate = useNavigate();
  const [plans, setPlans] = useState(null);
  const [yearly, setYearly] = useState(false);
  const [allPlans, setAllPlans] = useState(null);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        if (module) {
          const res = await axios.get(`${API}/api/plans/${module}`);
          setPlans(res.data);
        } else {
          const res = await axios.get(`${API}/api/plans`);
          setAllPlans(res.data);
        }
      } catch { }
    };
    fetchPlans();
  }, [module]);

  // Overview page: all modules
  if (!module && allPlans) {
    return (
      <div className="min-h-screen bg-[#060B14]" data-testid="pricing-overview-page">
        <nav className="flex items-center justify-between px-6 py-4 border-b border-[#1E293B]">
          <Link to="/" className="text-xl font-bold text-white">GLOTECH <span className="text-[#0099CC]">DIGITAL</span></Link>
          <Link to="/login">
            <Button variant="outline" className="border-[#2A3441] text-white hover:bg-[#151B2B]">Connexion</Button>
          </Link>
        </nav>
        <div className="max-w-6xl mx-auto px-4 py-16 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">Choisissez votre espace</h1>
          <p className="text-[#64748b] text-lg mb-12 max-w-2xl mx-auto">Un compte unique, des modules adaptes a vos besoins</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {Object.entries(allPlans).map(([key, modPlans]) => {
              const meta = MODULE_META[key] || {};
              const Icon = meta.icon || Monitor;
              return (
                <button
                  key={key}
                  onClick={() => navigate(`/pricing/${key}`)}
                  className="bg-[#0B0F19] border border-[#1E293B] rounded-2xl p-8 text-left hover:border-opacity-50 transition-all group"
                  style={{ "--border-color": meta.color }}
                  data-testid={`pricing-module-${key}`}
                >
                  <div className="w-14 h-14 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: meta.color + "15" }}>
                    <Icon className="w-7 h-7" style={{ color: meta.color }} />
                  </div>
                  <h2 className="text-xl font-bold text-white mb-2">{meta.label}</h2>
                  <p className="text-sm text-[#64748b] mb-4">{meta.tagline}</p>
                  <div className="text-sm text-[#94A3B8]">
                    {Object.keys(modPlans).length} plans disponibles
                  </div>
                  <div className="flex items-center gap-2 mt-4 text-sm font-medium group-hover:translate-x-1 transition-transform" style={{ color: meta.color }}>
                    Voir les tarifs <ArrowRight className="w-4 h-4" />
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // Module-specific pricing
  if (!plans) return null;

  const meta = MODULE_META[module] || {};
  const Icon = meta.icon || Monitor;

  const FEATURE_LABELS = {
    registration: "Inscriptions en ligne",
    qrcode: "QR Codes & Badges",
    access_control: "Controle d'acces",
    exports: "Export CSV/PDF",
    live_control: "Live Control (regie)",
    badges: "Badges personnalises",
    push_notifications: "Notifications push",
    priority_support: "Support prioritaire",
    api_access: "Acces API",
    custom_domain: "Domaine personnalise",
    crm_basic: "CRM & contacts",
    invoicing: "Facturation",
    proposals: "Propositions commerciales",
    seo: "Audit SEO & rapports",
    social: "Reseaux sociaux",
    ai_assistant: "Assistant IA",
    white_label: "Marque blanche",
    kanban: "Tableau Kanban",
    tasks: "Gestion des taches",
    timeline: "Timeline & Gantt",
    milestones: "Jalons & livrables",
    file_sharing: "Partage de fichiers",
    reports: "Rapports d'avancement",
  };

  return (
    <div className="min-h-screen bg-[#060B14]" data-testid="pricing-module-page">
      <nav className="flex items-center justify-between px-6 py-4 border-b border-[#1E293B]">
        <Link to="/" className="text-xl font-bold text-white">GLOTECH <span className="text-[#0099CC]">DIGITAL</span></Link>
        <div className="flex items-center gap-3">
          <Link to="/pricing"><Button variant="ghost" className="text-[#94A3B8] hover:text-white">Tous les services</Button></Link>
          <Link to="/login"><Button variant="outline" className="border-[#2A3441] text-white hover:bg-[#151B2B]">Connexion</Button></Link>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-4 py-12 sm:py-16">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full mb-4" style={{ backgroundColor: meta.color + "10" }}>
            <Icon className="w-5 h-5" style={{ color: meta.color }} />
            <span className="text-sm font-semibold" style={{ color: meta.color }}>{meta.label}</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3">{meta.tagline}</h1>
          <p className="text-[#64748b]">Choisissez le plan adapte a vos besoins</p>

          {/* Billing toggle */}
          <div className="flex items-center justify-center gap-3 mt-6">
            <span className={`text-sm ${!yearly ? "text-white" : "text-[#64748b]"}`}>Mensuel</span>
            <button
              onClick={() => setYearly(!yearly)}
              className={`relative w-12 h-6 rounded-full transition-colors ${yearly ? "bg-[#10B981]" : "bg-[#2A3441]"}`}
              data-testid="billing-toggle"
            >
              <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-transform ${yearly ? "translate-x-6" : "translate-x-0.5"}`} />
            </button>
            <span className={`text-sm ${yearly ? "text-white" : "text-[#64748b]"}`}>
              Annuel <span className="text-[#10B981] text-xs font-medium">-20%</span>
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {Object.entries(plans).map(([planKey, plan]) => {
            const PlanIcon = PLAN_ICONS[planKey] || Zap;
            const price = yearly ? plan.price_yearly : plan.price_monthly;
            const period = yearly ? "/an" : "/mois";
            const isPro = planKey === "pro";
            const allFeatures = Object.keys(FEATURE_LABELS);
            const planFeatures = plan.features || [];

            return (
              <div
                key={planKey}
                className={`relative bg-[#0B0F19] rounded-2xl p-6 border-2 transition-all ${
                  isPro ? "border-opacity-60 scale-[1.02] shadow-lg" : "border-[#1E293B]"
                }`}
                style={isPro ? { borderColor: meta.color } : undefined}
                data-testid={`plan-card-${planKey}`}
              >
                {isPro && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs font-bold text-white" style={{ backgroundColor: meta.color }}>
                    POPULAIRE
                  </div>
                )}
                <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold mb-4 ${PLAN_BADGE_COLORS[planKey]}`}>
                  <PlanIcon className="w-3.5 h-3.5" /> {PLAN_LABELS[planKey]}
                </div>
                <div className="mb-6">
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-bold text-white">{price === 0 ? "0" : price}</span>
                    {price > 0 && <span className="text-[#64748b] text-sm">EUR{period}</span>}
                    {price === 0 && <span className="text-[#64748b] text-sm">EUR</span>}
                  </div>
                  {plan.max_events != null && (
                    <p className="text-xs text-[#64748b] mt-2">
                      {plan.max_events === -1 ? "Evenements illimites" : `${plan.max_events} evenement${plan.max_events > 1 ? "s" : ""}`}
                      {plan.max_participants && (plan.max_participants === -1 ? " · Participants illimites" : ` · ${plan.max_participants} participants`)}
                    </p>
                  )}
                  {plan.max_clients != null && (
                    <p className="text-xs text-[#64748b] mt-2">
                      {plan.max_clients === -1 ? "Clients illimites" : `${plan.max_clients} clients`}
                    </p>
                  )}
                  {plan.max_projects != null && (
                    <p className="text-xs text-[#64748b] mt-2">
                      {plan.max_projects === -1 ? "Projets illimites" : `${plan.max_projects} projets`}
                    </p>
                  )}
                </div>

                {price === 0 ? (
                  <Link to={module === "event_center" ? "/event/signup" : "/register"}>
                    <Button className="w-full bg-[#1E293B] hover:bg-[#2A3441] text-white" data-testid={`cta-${planKey}`}>
                      Commencer gratuitement
                    </Button>
                  </Link>
                ) : (
                  <Link to={`/register?module=${module}&plan=${planKey}`}>
                    <Button className="w-full text-white" style={{ backgroundColor: meta.color }} data-testid={`cta-${planKey}`}>
                      Choisir ce plan <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                )}

                <div className="mt-6 space-y-2.5">
                  {allFeatures.filter(f => FEATURE_LABELS[f] && (module === "event_center" ? ["registration","qrcode","access_control","exports","live_control","badges","push_notifications","priority_support","api_access","custom_domain"].includes(f) : module === "web" ? ["crm_basic","invoicing","proposals","seo","social","ai_assistant","white_label","priority_support","api_access"].includes(f) : ["kanban","tasks","timeline","milestones","file_sharing","reports"].includes(f))).map(f => {
                    const has = planFeatures.includes(f);
                    return (
                      <div key={f} className="flex items-center gap-2.5 text-sm">
                        {has ? <Check className="w-4 h-4 text-[#10B981] shrink-0" /> : <X className="w-4 h-4 text-[#334155] shrink-0" />}
                        <span className={has ? "text-[#94A3B8]" : "text-[#334155]"}>{FEATURE_LABELS[f]}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
